import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, onMounted, computed } from 'vue';
import { InviteUserModel, UserModel, UserStatus, UserType, MeasureUnit, IUserData } from '@truckdown/accounts';

interface IData {
    isLoading: Ref<boolean>;
    showUsersModal: Ref<boolean>;

    users: Ref<UserModel[]>;
}

const getData = function () {
    return {
        isLoading: ref<boolean>(false),
        showUsersModal: ref<boolean>(false),

        users: ref<UserModel[]>([]),
        invitedUsers: ref<InviteUserModel[]>([])
    } as IData;
}

const getComputed = function (data: IData) {
    return {
        numInvited: computed(() => {
            const count = data.users.value.filter(u => u.status == UserStatus.Invited).length;
            if (count == 1) {
                return `${count} invite`;
            } else {
                return `${count} invites`;
            }
        }),
        numUsers: computed(() => {
            const count = data.users.value.filter(u => u.status != UserStatus.Invited).length;
            if (count == 1) {
                return `${count} user`;
            } else {
                return `${count} users`;
            }
        })
    };
}

const ADMIN_SERVICE_REQUEST_RIGHT = "FSRADMIN";

const getMethods = function (data: IData) {
    return {
        openUsersModal: function () {
            data.showUsersModal.value = true;
        },
        closeInvite: function (user?: InviteUserModel) {
            data.showUsersModal.value = false;
            if (user) {
                const model = {
                    id: user.email,
                    username: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    aid: user.accountId,
                    ain: context.user?.accountNumber ?? 0,
                    email: user.email,
                    status: UserStatus.Invited,
                    isLockedOut: false,
                    isAdmin: user.isAdmin,
                    isPrimary: false,
                    isBilling: user.isBilling,
                    modified: new Date(),
                    phones: { office: '' },
                    properties: { type: UserType.Fleet, timezone: '', measureUnit: MeasureUnit.Imperial, originalSite: 'truckdown', sites: [] },
                    roles: [],
                    claims: [],
                    settings: {

                    }
                } as UserModel;

                if (user.selectedFleetServiceRequestsRight && user.selectedFleetServiceRequestsRight.length > 0) {
                    model.settings["invite_fsr"] = user.selectedFleetServiceRequestsRight;
                }
                if (user.selectedVendorServiceRequestsRight && user.selectedVendorServiceRequestsRight.length > 0) {
                    model.settings["invite_vsr"] = user.selectedVendorServiceRequestsRight;
                }

                data.users.value.push(model);
            }
        },
        getUserRole: function (user: UserModel) {
            const roles: string[] = [];
            if (user.isAdmin) {
                roles.push('Admin');
            }
            if (!user.isAdmin && user.isBilling) {
                roles.push('Billing');
            }
            if (user.status == UserStatus.Invited && user.settings['invite_fsr'] == ADMIN_SERVICE_REQUEST_RIGHT) { //has claim or setting for service requests
                roles.push('Service Request Admin');
            }
            if (user.status == UserStatus.Active && user.claims.some(c => c.type == ADMIN_SERVICE_REQUEST_RIGHT && c.value == 'True')) { //has claim or setting for service requests
                roles.push('Service Request Admin');
            }
            if (roles.length == 0) {
                roles.push('User');
            }

            return roles.join(' + ');
        }
    }
}

const loadUsers = async function (data: IData) {
    data.isLoading.value = true;
    try {
        const service = await context.getService<IUserData>('IUserData');
        const users = await service.getAccountsUsersSummary(context.user?.accountNumber ?? 0);
        if (users.ok) {
            data.users.value = users.data?.filter(u => u.id != context.user?.id && (u.status == UserStatus.Active || u.status == UserStatus.Invited)) ?? [];
        } else {
            console.log('Error getting user list', users);
        }
    }
    catch (err) {
        console.log('Error getting user list', err);
    } finally {
        data.isLoading.value = false;
    }
}

export default defineComponent({
    props: {
    },
    name: "fleet-setup-team-setup",
    emits: [],
    components: {
    },
    setup: function (props: any, ctx: any) {
        const data = getData();
        const computed = getComputed(data);
        const methods = getMethods(data);

        onMounted(async () => {
            await loadUsers(data);
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});